<template>
  <v-dialog v-model="dialog" max-width="1600px" persistent>
    <div class="wage-nontax-compare-yearly-modal">
      <div class="modal-header">
        <h2>임금개선 분석 보고서 (1년치)</h2>
        <v-icon class="close-btn" @click="closeDialog">mdi-close</v-icon>
      </div>

      <div class="info-section">
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">조회 연도</span>
            <select v-model="selectedYear" class="year-select" @change="fetchYearlyData">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}년</option>
            </select>
          </div>
          <div class="info-item">
            <span class="info-label">데이터 현황</span>
            <span class="info-value">
              <span class="data-badge">{{ monthCount }}개월 데이터 보유</span>
            </span>
          </div>
        </div>
      </div>

      <div class="setting-section">
        <div class="setting-title">비과세 미적용 시나리오 설정</div>
        <div class="setting-row">
          <div class="setting-item lunch-setting">
            <label class="setting-label">중식비 금액 (전 직원 동일 적용)</label>
            <v-text-field
              v-model="customLunch"
              type="text"
              density="compact"
              variant="outlined"
              hide-details
              class="lunch-input"
              placeholder="0"
              @input="onSettingChange"
            />
            <span class="setting-hint">※ 현재 중식비가 더 작으면 현재 금액 유지</span>
          </div>
          <div class="setting-item">
            <v-checkbox
              v-model="noProductionNontax"
              label="생산직 비과세 모두 미적용 (연장/야간/휴일 수당 비과세 제외)"
              hide-details
              density="compact"
              color="primary"
              @change="onSettingChange"
            />
          </div>
        </div>
      </div>

      <div class="modal-content" v-if="loading">
        <div class="loading-state">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p>데이터를 불러오는 중...</p>
        </div>
      </div>

      <div class="modal-content" v-else-if="hasAnyData">
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="monthly">월별 상세</v-tab>
          <v-tab value="summary">연간 요약</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="monthly">
            <div class="unit-label">단위: 원</div>
            <div class="monthly-table-wrapper">
              <table class="monthly-table">
                <thead>
                  <tr>
                    <th class="sticky-col">월</th>
                    <th>대상 인원</th>
                    <th>총급여</th>
                    <th class="before-header">미적용 비과세</th>
                    <th class="before-header">미적용 기관부담</th>
                    <th class="before-header">미적용 퇴직금</th>
                    <th class="after-header">적용 비과세</th>
                    <th class="after-header">적용 기관부담</th>
                    <th class="after-header">적용 퇴직금</th>
                    <th class="diff-header">기관부담 차액(미적용-적용(현재))</th>
                    <th class="diff-header">퇴직금 차액(미적용-적용(현재))</th>
                    <th class="saving-header">월 절감액</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="monthData in monthlyData" :key="monthData.month" 
                      :class="{ 'no-data-row': !monthData.hasData }">
                    <td class="sticky-col">{{ monthData.month }}월</td>
                    <td class="text-center">{{ monthData.hasData ? monthData.employeeCount + '명' : '-' }}</td>
                    <td class="text-right">{{ monthData.hasData ? monthData.totalPay : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.nonTax : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.companyIns : '-' }}</td>
                    <td class="before-cell text-right">{{ monthData.hasData ? monthData.before.retirement : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.nonTax : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.companyIns : '-' }}</td>
                    <td class="after-cell text-right">{{ monthData.hasData ? monthData.after.retirement : '-' }}</td>
                    <td class="diff-cell text-right" :class="getDiffClass(monthData.diff?.companyIns, true)">
                      {{ monthData.hasData ? monthData.diff.companyIns : '-' }}
                    </td>
                    <td class="diff-cell text-right" :class="getDiffClass(monthData.diff?.retirement, true)">
                      {{ monthData.hasData ? monthData.diff.retirement : '-' }}
                    </td>
                    <td class="saving-cell text-right font-bold" :class="getDiffClass(monthData.saving, true)">
                      {{ monthData.hasData ? monthData.saving : '-' }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td class="sticky-col font-bold">연간 합계</td>
                    <td class="text-center font-bold">{{ monthCount }}개월</td>
                    <td class="text-right font-bold">{{ yearlyTotals.totalPay }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.nonTax }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.companyIns }}</td>
                    <td class="before-cell text-right font-bold">{{ yearlyTotals.before.retirement }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.nonTax }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.companyIns }}</td>
                    <td class="after-cell text-right font-bold">{{ yearlyTotals.after.retirement }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(yearlyTotals.diff.companyIns, true)">
                      {{ yearlyTotals.diff.companyIns }}
                    </td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(yearlyTotals.diff.retirement, true)">
                      {{ yearlyTotals.diff.retirement }}
                    </td>
                    <td class="saving-cell text-right font-bold" :class="getDiffClass(yearlyTotals.saving, true)">
                      {{ yearlyTotals.saving }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </v-window-item>

          <v-window-item value="summary">
            <div class="summary-section">
              <div class="summary-cards">
                <div class="summary-card before-card">
                  <h3>비과세 미적용</h3>
                  <div class="summary-row">
                    <span class="label">연간 총급여</span>
                    <span class="value">{{ yearlyTotals.totalPay }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 비과세</span>
                    <span class="value">{{ yearlyTotals.before.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">연간 기관부담 4대보험</span>
                    <span class="value">{{ yearlyTotals.before.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 퇴직금</span>
                    <span class="value">{{ yearlyTotals.before.retirement }}</span>
                  </div>
                </div>

                <div class="summary-card after-card">
                  <h3>비과세 적용 (현재)</h3>
                  <div class="summary-row">
                    <span class="label">연간 총급여</span>
                    <span class="value">{{ yearlyTotals.totalPay }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 비과세</span>
                    <span class="value">{{ yearlyTotals.after.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">연간 기관부담 4대보험</span>
                    <span class="value">{{ yearlyTotals.after.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">연간 퇴직금</span>
                    <span class="value">{{ yearlyTotals.after.retirement }}</span>
                  </div>
                </div>

                <div class="summary-card diff-card">
                  <h3>연간 기관 비용 절감 효과</h3>
                  <div class="summary-row highlight-row">
                    <span class="label">데이터 보유 기간</span>
                    <span class="value">{{ monthCount }}개월</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험 차액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.diff.companyIns, true)">{{ yearlyTotals.diff.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금 차액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.diff.retirement, true)">{{ yearlyTotals.diff.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">연간 절감액</span>
                    <span class="value large" :class="getDiffClass(yearlyTotals.saving, true)">{{ yearlyTotals.saving }}</span>
                  </div>
                  <div class="summary-row info">
                    <span class="label">월 평균 절감액</span>
                    <span class="value" :class="getDiffClass(yearlyTotals.saving, true)">{{ avgSaving }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </div>

      <div class="modal-content empty-state" v-else>
        <p>{{ selectedYear }}년 데이터가 없습니다.</p>
      </div>

      <div class="modal-footer">
        <v-btn variant="flat" class="btn-search" @click="generatePdf" :disabled="!hasAnyData" :loading="isGeneratingPdf">
          <v-icon left size="small">mdi-file-pdf-box</v-icon>
          PDF 출력
        </v-btn>
        <v-spacer />
        <v-btn variant="text" class="btn-cancel" @click="closeDialog">닫기</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import axios from "axios";
import { tokenManager } from "@/services/api";

interface MonthlyData {
  month: number;
  hasData: boolean;
  employeeCount: number;
  totalPay: string;
  before: {
    nonTax: string;
    companyIns: string;
    retirement: string;
  };
  after: {
    nonTax: string;
    companyIns: string;
    retirement: string;
  };
  diff: {
    companyIns: string;
    retirement: string;
  };
  saving: string;
}

const props = defineProps<{
  mCode?: string;
  memberName?: string;
  bizGubun?: string;
}>();

const dialog = ref(false);
const activeTab = ref("monthly");
const loading = ref(false);
const isGeneratingPdf = ref(false);
const selectedYear = ref(new Date().getFullYear());
const monthlyData = ref<MonthlyData[]>([]);
const monthCount = ref(0);
const customLunch = ref("");
const noProductionNontax = ref(false);

const yearlyTotals = reactive({
  totalPay: "0",
  before: {
    nonTax: "0", companyIns: "0", retirement: "0"
  },
  after: {
    nonTax: "0", companyIns: "0", retirement: "0"
  },
  diff: {
    companyIns: "0", retirement: "0"
  },
  saving: "0"
});

const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 2020; y--) {
    years.push(y);
  }
  return years;
});

const hasAnyData = computed(() => {
  return monthlyData.value.some(m => m.hasData);
});

const avgSaving = computed(() => {
  if (monthCount.value === 0) return "0";
  const total = parseFloat(yearlyTotals.saving.replace(/,/g, "")) || 0;
  return Math.round(total / monthCount.value).toLocaleString();
});

const openDialog = () => {
  dialog.value = true;
  activeTab.value = "monthly";
  selectedYear.value = new Date().getFullYear();
  customLunch.value = "";
  noProductionNontax.value = false;
  fetchYearlyData();
};

defineExpose({ openDialog });

const closeDialog = () => {
  dialog.value = false;
};

const onSettingChange = () => {
  fetchYearlyData();
};

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, "")) || 0;
};

const getDiffClass = (value: string | undefined, _isReverse = false): string => {
  if (!value) return "";
  const num = nc(value);
  // 차액 = 미적용 - 적용 → 양수가 절감(좋음)
  if (num > 0) return "highlight-green";
  if (num < 0) return "highlight-red";
  return "";
};

const fetchYearlyData = async () => {
  if (!props.mCode) {
    console.warn('시설 코드(m_code)가 없습니다.');
    return;
  }
  
  loading.value = true;
  try {
    const params = new URLSearchParams();
    if (props.bizGubun) {
      params.append('bizGubun', props.bizGubun);
    }
    
    // 설정값 전달
    const inputLunch = parseFloat(customLunch.value.toString().replace(/,/g, "")) || 0;
    if (customLunch.value !== "" && inputLunch >= 0) {
      params.append('customLunch', String(inputLunch));
    }
    if (noProductionNontax.value) {
      params.append('noProductionNontax', '1');
    }
    
    let url = `/api/common-wage/nontax-compare-yearly/${props.mCode}/${selectedYear.value}`;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const token = tokenManager.getToken();
    const response = await axios.get(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (response.data.success) {
      monthlyData.value = response.data.data.monthlyData;
      monthCount.value = response.data.data.monthCount;
      
      const yt = response.data.data.yearlyTotals;
      yearlyTotals.totalPay = yt.totalPay;
      yearlyTotals.before.nonTax = yt.before.nonTax;
      yearlyTotals.before.companyIns = yt.before.companyIns;
      yearlyTotals.before.retirement = yt.before.retirement;
      yearlyTotals.after.nonTax = yt.after.nonTax;
      yearlyTotals.after.companyIns = yt.after.companyIns;
      yearlyTotals.after.retirement = yt.after.retirement;
      yearlyTotals.diff.companyIns = yt.diff.companyIns;
      yearlyTotals.diff.retirement = yt.diff.retirement;
      yearlyTotals.saving = yt.saving;
    }
  } catch (error: any) {
    console.error("연간 데이터 조회 오류:", error);
    alert("데이터 조회 중 오류가 발생했습니다.");
  } finally {
    loading.value = false;
  }
};

const generatePdf = async () => {
  if (!hasAnyData.value) {
    alert("데이터가 없습니다.");
    return;
  }

  isGeneratingPdf.value = true;
  try {
    const token = tokenManager.getToken();
    const response = await axios.post("/api/common-wage/nontax-compare-yearly-pdf", {
      monthlyData: monthlyData.value,
      yearlyTotals: {
        totalPay: yearlyTotals.totalPay,
        before: yearlyTotals.before,
        after: yearlyTotals.after,
        diff: yearlyTotals.diff,
        saving: yearlyTotals.saving
      },
      memberName: props.memberName || "기관명",
      year: selectedYear.value
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    if (response.data.success) {
      const pdfBase64 = response.data.pdf;
      const binaryString = window.atob(pdfBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");
      if (!newWindow) {
        alert("팝업 차단 설정을 확인해주세요.");
      }
    } else {
      alert("PDF 생성 실패: " + (response.data.message || "알 수 없는 오류"));
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || "알 수 없는 오류";
    alert("PDF 생성 중 오류: " + message);
    console.error("PDF 생성 오류:", error);
  } finally {
    isGeneratingPdf.value = false;
  }
};
</script>
